import { AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react'
import { cn } from '../../lib/utils'

interface IdentificationFmvSummaryProps {
  totalIdentifiedFMV: number
  relinquishedFMV: number | null
}

type FmvStatus = 'no-fmv' | 'below-fmv' | 'sufficient' | 'over-200'

function resolveFmvStatus(
  totalFMV: number,
  relinquishedFMV: number | null
): FmvStatus {
  if (relinquishedFMV == null || relinquishedFMV <= 0) return 'no-fmv'
  if (totalFMV > relinquishedFMV * 2) return 'over-200'
  if (totalFMV >= relinquishedFMV) return 'sufficient'
  return 'below-fmv'
}

function formatDollars(value: number): string {
  return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export function IdentificationFmvSummary({
  totalIdentifiedFMV,
  relinquishedFMV,
}: IdentificationFmvSummaryProps) {
  const status = resolveFmvStatus(totalIdentifiedFMV, relinquishedFMV)

  if (status === 'no-fmv') {
    return (
      <div
        className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3"
        role="status"
        aria-label="FMV validation unavailable"
      >
        <AlertTriangle
          className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
          aria-hidden="true"
        />
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Set the relinquished property FMV in the Sales Hub to enable this validation.
        </p>
      </div>
    )
  }

  const requiredFMV = relinquishedFMV!
  const maxFMV = requiredFMV * 2
  const progressPercent = Math.min(100, (totalIdentifiedFMV / maxFMV) * 100)

  const statusConfig = {
    'below-fmv': {
      containerClass:
        'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
      barClass: 'bg-red-500',
      labelClass: 'text-red-800 dark:text-red-200',
      valueClass: 'text-red-700 dark:text-red-300',
      Icon: AlertTriangle,
      iconClass: 'text-red-600 dark:text-red-400',
      statusText: 'Below relinquished FMV — exchange may not be tax-deferred',
    },
    sufficient: {
      containerClass:
        'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20',
      barClass: 'bg-green-500',
      labelClass: 'text-green-800 dark:text-green-200',
      valueClass: 'text-green-700 dark:text-green-300',
      Icon: CheckCircle,
      iconClass: 'text-green-600 dark:text-green-400',
      statusText: totalIdentifiedFMV <= maxFMV
        ? 'Sufficient (Standard 3-property rule)'
        : 'Properly matched',
    },
    'over-200': {
      containerClass:
        'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
      barClass: 'bg-red-500',
      labelClass: 'text-red-800 dark:text-red-200',
      valueClass: 'text-red-700 dark:text-red-300',
      Icon: AlertTriangle,
      iconClass: 'text-red-600 dark:text-red-400',
      statusText: 'Exceeds 200% rule — must satisfy 95% rule',
    },
  } as const

  const config = statusConfig[status]
  const { Icon } = config

  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3 space-y-2',
        config.containerClass
      )}
      role="status"
      aria-label="FMV constraint summary"
    >
      <div className="flex items-start gap-2">
        <Icon
          className={cn('h-4 w-4 flex-shrink-0 mt-0.5', config.iconClass)}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={cn('text-xs font-semibold', config.labelClass)}>
              Identified Total
            </span>
            <span className={cn('text-sm font-bold', config.valueClass)}>
              {formatDollars(totalIdentifiedFMV)}
              <span className={cn('text-xs font-normal ml-1', config.labelClass)}>
                / Required: {formatDollars(requiredFMV)}
              </span>
            </span>
          </div>
          <div
            className="mt-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/10 overflow-hidden"
            role="progressbar"
            aria-valuenow={Math.round(progressPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="FMV progress toward 200% limit"
          >
            <div
              className={cn('h-full rounded-full transition-all duration-300', config.barClass)}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex items-center gap-1 mt-1.5">
            <TrendingUp className={cn('h-3 w-3', config.iconClass)} aria-hidden="true" />
            <p className={cn('text-xs', config.labelClass)}>{config.statusText}</p>
          </div>
          <p className={cn('text-[10px] mt-0.5', config.labelClass)}>
            200% limit: {formatDollars(maxFMV)}
          </p>
        </div>
      </div>
    </div>
  )
}
